/**
 * RestoID — passwordless verification code (SignIn / Register / ForgotPassword).
 * ---------------------------------------------------------------------------
 * RestoID frame (head + "sent to … · edit" chip) over the upstream verification
 * hooks — `codeVerificationHooks` for submit and `useResendVerificationCode` for
 * the timer. ForgotPassword and Continue are upstream's untouched; SignIn and
 * Register are RestoID's `use-sign-in-auto-register` and `use-register-auto-sign-in`,
 * which drop upstream's two confirmation modals so an identifier with no account
 * registers and one that already has an account signs in (each hook's header sets
 * out the reasoning and what it keeps). The markup (OTP boxes, chip,
 * resend row) is ours, shared with the MFA screens through
 * `custom/components/OtpEntry`; the upstream `VerificationCodeContainer` is
 * deliberately not used.
 * Guards mirror the upstream page so an invalid session still routes to the same
 * ErrorPage.
 * Swapped in for `./pages/VerificationCode` via `src/App.tsx`.
 */
import { SignInIdentifier, type VerificationCodeIdentifier } from '@logto/schemas';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { validate } from 'superstruct';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import SwitchToVerificationMethodsLink from '@/components/SwitchToVerificationMethodsLink';
import useResendVerificationCode from '@/containers/VerificationCode/use-resend-verification-code';
import { codeVerificationHooks } from '@/containers/VerificationCode/utils';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import useToast from '@/hooks/use-toast';
import { type IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';
import { UserFlow } from '@/types';
import { userFlowGuard } from '@/types/guard';
import { formatPhoneNumberWithCountryCallingCode } from '@/utils/country-code';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

import { OtpEntry, SentToChip, isOtpReady } from '../components/OtpEntry';
import { ScreenHead, Stack } from '../components/restoid';
import useRegisterAutoSignIn from '../components/use-register-auto-sign-in';
import useSignInAutoRegister from '../components/use-sign-in-auto-register';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

type Parameters = {
  flow: string;
};

/**
 * Upstream's flow -> hook map with TWO arms replaced. ForgotPassword and Continue
 * keep upstream's hooks by spreading, so upstream changes to them arrive through
 * `pnpm sync-upstream` without this file being touched. The two replacements are
 * COPIES of upstream logic and do NOT — see `## Caveats` in RESTOID-DESIGN-PLAN.md.
 */
const restoCodeVerificationHooks = Object.freeze({
  ...codeVerificationHooks,
  [UserFlow.SignIn]: useSignInAutoRegister,
  [UserFlow.Register]: useRegisterAutoSignIn,
});

const isValidVerificationCodeIdentifier = (
  identifierInputValue: IdentifierInputValue | undefined
): identifierInputValue is VerificationCodeIdentifier =>
  Boolean(
    identifierInputValue?.type &&
    identifierInputValue.type !== SignInIdentifier.Username &&
    identifierInputValue.value
  );

/** "+7 9111978787" → "+7 (911) 197-87-87" (RU mask, matches the sign-in PhoneInput);
 *  any other shape passes through unchanged. */
const maskPhone = (formatted: string): string => {
  const ru = /^\+7(\d{3})(\d{3})(\d{2})(\d{2})$/.exec(formatted.replace(/\s+/g, ''));
  return ru ? `+7 (${ru[1]}) ${ru[2]}-${ru[3]}-${ru[4]}` : formatted;
};

/**
 * OTP entry + resend — the design's layout (auto-submit, no "Continue" button,
 * concise resend copy), reusing the upstream flow hooks for ALL submit/resend/
 * error logic so it stays in sync with upstream. The exceptions are the SignIn and
 * Register submit hooks, substituted above.
 */
const CodeBody = ({
  flow,
  identifier,
  verificationId,
  hasPasswordButton,
}: {
  flow: UserFlow;
  identifier: VerificationCodeIdentifier;
  verificationId: string;
  hasPasswordButton?: boolean;
}) => {
  const { setToast } = useToast();
  const [codeInput, setCodeInput] = useState<string[]>([]);
  const isCodeInputReady = isOtpReady(codeInput);

  const useVerificationCode = restoCodeVerificationHooks[flow];
  const errorCallback = useCallback(() => {
    setCodeInput([]);
  }, []);
  const {
    errorMessage: submitErrorMessage,
    clearErrorMessage,
    onSubmit,
  } = useVerificationCode(identifier, verificationId, errorCallback);

  // Surface a bad/expired code as a toast (the design pattern), not an inline error.
  useEffect(() => {
    if (submitErrorMessage) {
      setToast(submitErrorMessage);
    }
  }, [submitErrorMessage, setToast]);

  const { seconds, isRunning, onResendVerificationCode } = useResendVerificationCode(
    flow,
    identifier
  );

  // Same sequence the inline resend row ran: wipe the prior error, resend, then
  // empty the boxes so the new code is typed into a clean row.
  const handleResend = useCallback(async () => {
    clearErrorMessage();
    await onResendVerificationCode();
    setCodeInput([]);
  }, [clearErrorMessage, onResendVerificationCode]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = useCallback(
    async (code: string[]) => {
      if (isSubmitting) {
        return;
      }
      // Clear the prior error first so an identical repeat error still re-fires the toast.
      clearErrorMessage();
      setIsSubmitting(true);
      try {
        await onSubmit(code.join(''));
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, onSubmit, clearErrorMessage]
  );

  // Auto-submit on full entry via a ref so the effect doesn't depend on
  // handleSubmit's identity (rebuilt mid-flow) — mirrors the upstream container.
  const handleSubmitRef = useRef(handleSubmit);

  handleSubmitRef.current = handleSubmit;
  useEffect(() => {
    if (isCodeInputReady) {
      void handleSubmitRef.current(codeInput);
    }
  }, [codeInput, isCodeInputReady]);

  return (
    <>
      {/* No `error`: a bad/expired code is surfaced as a toast above, not inline. */}
      <OtpEntry
        name="passcode"
        value={codeInput}
        onChange={setCodeInput}
        seconds={seconds}
        isRunning={isRunning}
        onResend={() => {
          void handleResend();
        }}
      />
      {flow === UserFlow.SignIn ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <SwitchToVerificationMethodsLink
            hasPassword={hasPasswordButton}
            identifier={identifier.type}
            value={identifier.value}
          />
        </div>
      ) : null}
    </>
  );
};

const VerificationCode = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const { flow } = useParams<Parameters>();
  const { signInMethods } = useSieMethods();
  const navigate = useNavigateWithPreservedSearchParams();

  const { identifierInputValue, forgotPasswordIdentifierInputValue, verificationIdsMap } =
    useContext(UserInteractionContext);

  const [, userFlow] = validate(flow, userFlowGuard);

  if (!userFlow) {
    return <ErrorPage />;
  }

  const cachedIdentifierInputValue =
    flow === UserFlow.ForgotPassword ? forgotPasswordIdentifierInputValue : identifierInputValue;

  if (!isValidVerificationCodeIdentifier(cachedIdentifierInputValue)) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { type, value } = cachedIdentifierInputValue;

  const methodSettings = signInMethods.find((method) => method.identifier === type);
  const hasPasswordButton = userFlow === UserFlow.SignIn && methodSettings?.password;

  const verificationId = verificationIdsMap[codeVerificationTypeMap[type]];
  if (!verificationId) {
    return <ErrorPage title="error.invalid_session" rawMessage="Verification ID not found" />;
  }

  const target =
    type === SignInIdentifier.Phone
      ? maskPhone(formatPhoneNumberWithCountryCallingCode(value))
      : value;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: `description.verify_${type}` }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('action.enter_passcode')}
        />
        <SentToChip target={target} onEdit={() => navigate(-1)} />
        <CodeBody
          flow={userFlow}
          identifier={cachedIdentifierInputValue}
          verificationId={verificationId}
          hasPasswordButton={hasPasswordButton}
        />
      </Stack>
    </FirstScreenLayout>
  );
};

export default VerificationCode;
